import { useEffect, useState } from 'react'
import { Country, State, States } from '@swagger'
import { addressApi } from '@api/swagger'
import countriesAndStates from '../helpers/placeholderData/countriesAndStates'
import useLoadingContext from './useLoadingContext'

export const sortByName = (
  firstStateOrCountry: State | Country,
  secondStateOrCountry: State | Country
): -1 | 0 | 1 =>
  firstStateOrCountry.name < secondStateOrCountry.name
    ? -1
    : firstStateOrCountry.name > secondStateOrCountry.name
    ? 1
    : 0

const fallbackStates = countriesAndStates.US.map(({ label, value }) => ({
  name: label,
  stateCode: value,
  countryCode: 'US',
}))

export const useStatesForCountry = (props: {
  countryCode: string
}): { defaultStates: State[]; triggerFetchStates: () => void } => {
  const { countryCode } = props

  const [
    states /* `setStates` is a function provided by the `useState` hook in
  React that is used to update the state value of `states`. In
  this code, it is used to update the list of states fetched from
  the API or the fallback list of states if the API call fails. */,
    setStates,
  ] = useState<State[]>([])
  const fetchStates = async () => {
    if (!!countryCode) {
      let fetchedStates: States
      try {
        fetchedStates = await addressApi.fetchStates({
          countryCode: countryCode,
        })
      } catch {
        fetchedStates = {}
      }

      setStates(fetchedStates?.data ?? fallbackStates)
    }
  }

  const { triggerFetch: triggerFetchStates } = useLoadingContext({
    asyncFunction: fetchStates,
    loadingId: 'FetchStates',
  })

  useEffect(() => {
    if (countryCode !== '') {
      triggerFetchStates()
    }
  }, [countryCode, triggerFetchStates])

  return { defaultStates: states.sort(sortByName), triggerFetchStates }
}

export default useStatesForCountry
